import React, { useRef } from 'react';
import { useAudioPlayer } from './AudioPlayerContext';
import { PlayCircleFilled, PauseCircleFilled, CloseOutlined, SoundOutlined } from '@ant-design/icons';

function formatTime(sec) {
  if (!sec || isNaN(sec)) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function MiniPlayer() {
  const { track, playing, progress, duration, currentTime, pause, resume, seek, close } = useAudioPlayer();
  const barRef = useRef(null);

  if (!track) return null;

  const handleBarClick = (e) => {
    const rect = barRef.current.getBoundingClientRect();
    const pct = ((e.clientX - rect.left) / rect.width) * 100;
    seek(Math.max(0, Math.min(100, pct)));
  };

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999,
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      color: '#fff', padding: '0', boxShadow: '0 -4px 24px rgba(0,0,0,0.3)',
      borderTop: '2px solid #667eea',
    }}>
      {/* Progress bar (clickable) */}
      <div ref={barRef} onClick={handleBarClick}
        style={{ height: 4, background: 'rgba(255,255,255,0.15)', cursor: 'pointer', position: 'relative' }}>
        <div style={{
          height: '100%', background: 'linear-gradient(90deg, #667eea, #764ba2)',
          width: `${progress}%`, transition: 'width 0.3s linear', borderRadius: 2,
        }} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', padding: '8px 16px', gap: 12 }}>
        {/* Rotating disc */}
        <div style={{
          width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
          background: 'linear-gradient(135deg, #667eea, #764ba2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: playing ? 'spin 3s linear infinite' : 'none',
          boxShadow: '0 0 12px rgba(102,126,234,0.5)',
        }}>
          <div style={{
            width: 16, height: 16, borderRadius: '50%', background: '#1a1a2e',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <SoundOutlined style={{ fontSize: 8, color: '#667eea' }} />
          </div>
        </div>

        {/* Song info */}
        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <div style={{
            fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap',
            overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {track.title || 'Unknown Track'}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', display: 'flex', gap: 8 }}>
            {track.artist && <span>{track.artist}</span>}
            <span>{formatTime(currentTime)} / {formatTime(duration)}</span>
          </div>
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <div onClick={playing ? pause : resume}
            style={{ cursor: 'pointer', fontSize: 36, lineHeight: 1, color: '#667eea', transition: 'transform 0.2s' }}
            onMouseEnter={e => e.target.style.transform = 'scale(1.15)'}
            onMouseLeave={e => e.target.style.transform = 'scale(1)'}>
            {playing ? <PauseCircleFilled /> : <PlayCircleFilled />}
          </div>
          <div onClick={close}
            style={{
              cursor: 'pointer', width: 28, height: 28, borderRadius: '50%',
              background: 'rgba(255,255,255,0.1)', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,77,79,0.3)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}>
            <CloseOutlined style={{ fontSize: 12 }} />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
